// sort-imports-ignore
import { IonButton, IonIcon, IonRouterLink, IonText } from '@ionic/react';
import React, { useCallback, useMemo } from 'react';

import ButtonContainer from '@/components/common/buttonContainer/ButtonContainer';
import Spacer from '@/components/common/spacer/Spacer';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import useToast from '@/hooks/useToast';
import { SCHEDULE_FLOW_STEPS } from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import {
  arrowForwardCircleOutline,
  chevronBack,
  chevronForward,
  informationCircleOutline
} from 'ionicons/icons';
import moment from 'moment';
import Calendar from 'react-calendar';
import { Value } from 'react-calendar/dist/cjs/shared/types';

import 'react-calendar/dist/Calendar.css';
import './SelectDate.css';
import { isClinicName } from '@/utils/helpers/isClinicName';

const SelectDate: React.FC = () => {
  const {
    dentists,
    selectedDentist,
    selectedDate,
    setSelectedDate,
    availableDates,
    setCurrentStep
  } = useScheduleFlow();

  const isUsingClinicName = useMemo(() => {
    return isClinicName(
      `${selectedDentist?.first_name} ${selectedDentist?.last_name}`
    );
  }, [selectedDentist?.first_name, selectedDentist?.last_name]);

  const displayToast = useToast();
  const copy = {
    subHeader: 'SELECT YOUR PREFERRED DATE',
    header: (
      <>
        Pick a Day for your <IonText color="primary">dentist</IonText>{' '}
        appointment
      </>
    ),
    buttonText: 'Next'
  };

  const isDisabled = useCallback(
    (date: Date) => {
      return !availableDates.some((availableDate) =>
        moment(availableDate).isSame(date, 'day')
      );
    },
    [availableDates]
  );

  const onSelect = (day: Value) => {
    const date = new Date(day as Date);
    if (isDisabled(date)) {
      displayToast({
        message:
          'The date you clicked is not available. Please select another date or dentist.',
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    } else {
      setSelectedDate(moment(date).isSame(selectedDate, 'day') ? null : date);
    }
  };

  const tileClassName = ({ date }: { date: Date }) => {
    return isDisabled(date) ? 'disabled' : 'enabled';
  };

  const onNext = () => {
    if (selectedDate) {
      setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_TIME);
    } else {
      displayToast({
        message: 'Please select an available date to proceed.',
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  };

  const renderNote = () => {
    if (dentists.length > 1) {
      return (
        <div id="note">
          <div id="noteIcon">
            <IonIcon icon={informationCircleOutline} />
          </div>
          <div id="noteText">
            <IonText>Note:</IonText> If{' '}
            {formatDentistName({
              dentist: selectedDentist,
              returnDentistFullName: true
            })}{' '}
            is not available on your desired date, you can{' '}
            <IonRouterLink
              onClick={() => setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST)}
            >
              choose a different {isUsingClinicName ? 'clinic' : 'dentist'}
            </IonRouterLink>
            .
          </div>
        </div>
      );
    }
  };

  return (
    <div id="selectDate">
      <Spacer top={32} bottom={32}>
        <h4 id="subHeader">{copy.subHeader}</h4>
        <h1 id="header">{copy.header}</h1>
      </Spacer>
      <Calendar
        onChange={onSelect}
        onClickDay={onSelect}
        tileClassName={tileClassName}
        minDate={moment().subtract(1, 'months').toDate()}
        maxDetail="month"
        minDetail="month"
        prevLabel={<IonIcon icon={chevronBack} color="primary" />}
        nextLabel={<IonIcon icon={chevronForward} color="primary" />}
        value={selectedDate ? moment(selectedDate).toDate() : null}
        showNeighboringMonth={false}
        calendarType="gregory"
      />
      {renderNote()}
      <Spacer top={102} />
      <ButtonContainer>
        <IonButton
          id="nextButton"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          strong={true}
        >
          <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default SelectDate;
